import React from 'react';
import { TrendingDown, TrendingUp } from 'lucide-react';

import { Badge } from 'shadcn/components/ui/Badge';

import { ProjectedIssueRecommendedAction } from './ProjectedIssueRecommendedAction';

/**
 * Summary section of a projected issue card
 * Shows SKU, product name, severity, issue type, and quantity
 *
 * @param {Object} props
 * @param {string} props.externalSku - SKU identifier (e.g., "111083")
 * @param {string} props.name - Product name (e.g., "Avocados")
 * @param {string} props.severity - Severity level: "High", "Medium", "Low"
 * @param {string} props.issueType - Type of issue: "Short", "Overflow"
 * @param {number} props.quantity - Quantity of the issue
 * @param {string} props.issueChannel - Channel with issue: "Wholesale", "First Party"
 * @param {string} props.recommendedAction - Recommended action: "TRANSFER", "BUY"
 * @param {boolean} props.isActive - Whether the card is active
 * @param {boolean} props.isSelected - Whether the card is selected
 * @param {Function} props.onApplyRecommendedAction
 */
export function ProjectedIssueSummary({
    externalSku,
    isActive,
    issueChannel,
    issueType,
    name,
    onApplyRecommendedAction,
    quantity,
    recommendedAction,
    severity,
}) {
    const severityColors = {
        High: 'bg-red-100 text-red-700 border-red-300',
        Medium: 'bg-orange-100 text-orange-700 border-orange-300',
        Low: 'bg-yellow-100 text-yellow-700 border-yellow-300',
    };

    const issueTypeColors = {
        Short: 'bg-red-100 text-red-700 border-red-300',
        Overflow: 'bg-blue-100 text-blue-700 border-blue-300',
    };

    return (
        <div className={`p-4 pl-12 h-full ${isActive ? 'bg-[#e3ecf4]' : 'bg-stone-50 '}`}>
            <div className="flex flex-col pb-4 pt-2 h-full">
                {/* Header: SKU/Product Name and Badges */}
                <div className="flex justify-between items-start mb-4">
                    {/* SKU and Product Name */}
                    <div>
                        <div className="text-lg font-bold text-gray-900">SKU-{externalSku}</div>
                        <div className="text-base text-gray-600">{name}</div>
                    </div>

                    {/* Badges */}
                    <div className="flex gap-2">
                        <Badge className={`px-3 py-1 ${severityColors[severity] || 'bg-gray-100 text-gray-700'}`}>
                            {severity.toLowerCase()}
                        </Badge>
                        <Badge className={`px-3 py-1 ${issueTypeColors[issueType] || 'bg-gray-100 text-gray-700'}`}>
                            {issueType === 'Short' ? <TrendingDown size={14} /> : <TrendingUp size={14} />}
                            {issueType.toLowerCase()}
                        </Badge>
                    </div>
                </div>

                {/* Shortfall/Overflow Amount */}
                <div className="mb-4">
                    <div className="text-sm text-gray-600">{issueType === 'Short' ? 'Shortfall' : 'Overflow'}</div>
                    <div
                        className={`text-lg font-bold tabular-nums ${issueType === 'Short' ? 'text-[#d40824]' : 'text-[#0074a9]'}`}
                    >
                        {quantity.toLocaleString()} units
                    </div>
                </div>

                {/* Recommended Action - with background */}
                <div>
                    <ProjectedIssueRecommendedAction
                        recommendedAction={recommendedAction}
                        quantity={quantity}
                        issueType={issueType}
                        issueChannel={issueChannel}
                        onApply={onApplyRecommendedAction}
                    />
                </div>
            </div>
        </div>
    );
}
